/**
 * Notes et évals d'un élève, côté prof (lecture seule — migration 0322 : le
 * prof lié par un `tutor_links` actif les lit, l'élève reste seul à écrire).
 * Moyenne, prochaines évals avec les SKILLCARDS à réviser, dernières notes.
 */

import { useMemo } from 'react';
import { MathText } from '@jeraisonne/ui';
import { useMyEvaluations } from '@/lib/evaluations/api';
import { useStudentSkillcards } from '@/lib/cards/student-skillcards';
import {
  bucketEvaluations,
  evaluationDateLabel,
  formatGradeNumber,
  formatScore,
  weightedAverage,
} from '@/lib/evaluations/grades';
import { UpcomingEvaluation } from './evaluations-page';
import { EvaluationPhotos } from './evaluation-photos';
import { useStudentEvaluationPhotos } from '@/lib/evaluations/photos';

export function TutorStudentEvaluations({
  studentId,
  studentName,
}: {
  studentId: string;
  studentName: string;
}) {
  const evals = useMyEvaluations(studentId);
  const { data: skillcards = [] } = useStudentSkillcards(studentId);
  const masteryById = useMemo(
    () =>
      new Map(skillcards.filter((c) => c.usage_count > 0).map((c) => [c.id, c.mastery] as const)),
    [skillcards],
  );
  const list = evals.data ?? [];
  const { upcoming, toGrade, graded } = bucketEvaluations(list);
  const average = weightedAverage(graded);
  const photos = useStudentEvaluationPhotos(studentId);
  const photosOf = (evaluationId: string) =>
    (photos.data ?? []).filter((p) => p.evaluation_id === evaluationId);

  return (
    <section className="mb-6" aria-labelledby="tutor-student-evals">
      <h2
        id="tutor-student-evals"
        className="font-display text-ink-500 mb-3 text-[11px] font-bold uppercase tracking-[.1em]"
      >
        Notes et évals
      </h2>
      {evals.isLoading ? (
        <div className="bg-ink-100 h-20 animate-pulse rounded-2xl" />
      ) : list.length === 0 ? (
        <div className="border-ink-200 rounded-2xl border bg-white p-5 text-center">
          <p className="text-ink-400 text-sm">
            {studentName} n’a pas encore saisi d’éval ni de note.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="border-ink-100 ocean:bg-white/5 flex items-baseline justify-between gap-3 rounded-2xl border bg-white p-4">
            <span className="text-ink-600 text-sm">Moyenne</span>
            <span className="font-display text-ink-900 text-2xl font-extrabold">
              {formatGradeNumber(average)}
              <span className="text-ink-500 text-sm font-bold"> / 20</span>
            </span>
          </div>
          {upcoming.slice(0, 3).map((e) => (
            <UpcomingEvaluation key={e.id} evaluation={e} masteryById={masteryById} maxCards={4} />
          ))}
          {toGrade.length > 0 && (
            <p className="text-ink-500 text-sm">
              {toGrade.length} éval{toGrade.length > 1 ? 's' : ''} passée
              {toGrade.length > 1 ? 's' : ''} sans note saisie.
            </p>
          )}
          {graded.length > 0 && (
            <ul className="space-y-2">
              {graded.slice(0, 8).map((e) => (
                <li
                  key={e.id}
                  className="border-ink-100 ocean:bg-white/5 space-y-2 rounded-2xl border bg-white p-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="text-ink-900 block truncate font-semibold">
                        <MathText>{e.title}</MathText>
                      </span>
                      <span className="text-ink-500 block truncate text-sm">
                        {evaluationDateLabel(e.eval_date)}
                        {e.subject ? ` · ${e.subject}` : ''}
                        {e.coefficient !== 1 ? ` · coef ${formatGradeNumber(e.coefficient)}` : ''}
                      </span>
                    </span>
                    <span className="text-ink-900 shrink-0 font-bold">{formatScore(e)}</span>
                  </div>
                  <EvaluationPhotos
                    studentId={studentId}
                    evaluationId={e.id}
                    photos={photosOf(e.id)}
                    readOnly
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
